import { FC, memo } from 'react';
import { Link } from 'react-router-dom';
import { Menu, MenuItemProps } from 'antd';
import CountRequestBadge from 'components/CountRequestBadge';
import CountUnreadInboxBadge from 'components/CountUnreadInboxBadge';
import CountTaskBadge from 'components/CountTaskBadge';
import CountPendingBadge from 'components/CountPendingBadge';
import { checkHasPermission, checkHasRole } from 'utils/auth';
import { UserResponse } from 'types';
import { MenuItemBaseLinkType, MenuItemType } from './config';

interface AppMenuItemProps {
  item: MenuItemType;
  user: UserResponse;
  classNameLabel?: string;
  selectedItem?: MenuItemBaseLinkType;
  isSuperUser?: boolean;
  onClick?: MenuItemProps['onClick'];
  collapsed?: boolean;
  onAdminClick?: () => void;
}

const MenuItem: FC<AppMenuItemProps> = memo(({
  item,
  user,
  classNameLabel,
  selectedItem,
  isSuperUser,
  onClick,
  collapsed,
  onAdminClick,
}) => {
  const allowed =
    (!item.permission || (item.permission && checkHasPermission(user, item.permission))) &&
    (!item.role || (item.role && checkHasRole(user, item.role))) &&
    (!item.type || item.type === user.contact.type);

  if (!allowed || (item.needsSuperUser && !isSuperUser)) {
    return null;
  }

  if ('submenu' in item) {
    const isSubItemVisible = (subItem: MenuItemBaseLinkType) =>
      (!subItem.needsSuperUser || !!isSuperUser) &&
      (!subItem.permission || checkHasPermission(user, subItem.permission)) &&
      (!subItem.role || checkHasRole(user, subItem.role));

    const visibleSubItems = item.submenu.filter(isSubItemVisible);

    if (visibleSubItems.length === 0) return null;

    const childSelected = visibleSubItems.find((subItem) => subItem.path === selectedItem?.path);

    const handleAdminTitleClick = () => {
      if (collapsed && onAdminClick) {
        onAdminClick();
      }
    };

    return (
      <Menu.SubMenu
        key={item.key}
        icon={item.icon}
        title={<span className={classNameLabel}>{item.label}</span>}
        className={childSelected ? 'app-menu-submenu-selected' : undefined}
        onTitleClick={handleAdminTitleClick}
      >
        {visibleSubItems.map((subItem) => (
          <Menu.Item
            key={subItem.path}
            className={selectedItem?.path === subItem.path ? 'app-menu-item-selected' : undefined}
            onClick={onClick}
          >
            <Link className={classNameLabel} to={subItem.path}>
              {subItem.label}
            </Link>
          </Menu.Item>
        ))}
      </Menu.SubMenu>
    );
  }

  const showBadgeCompact = collapsed && 'showBadgeWhenCollapsed' in item && item.showBadgeWhenCollapsed;
  const iconWithBadge = showBadgeCompact ? (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {item.icon}
      {item.label === 'Tasks' && <CountTaskBadge collapsed />}
      {item.label === 'Requests' && <CountRequestBadge collapsed />}
      {item.label === 'Pending' && <CountPendingBadge collapsed />}
      {item.label === 'Mentions' && <CountUnreadInboxBadge collapsed />}
    </div>
  ) : (
    item.icon
  );

  const linkContent = !collapsed && (
    <>
      <span className={classNameLabel}>{item.label}</span>
      {item.label === 'Tasks' && <CountTaskBadge />}
      {item.label === 'Requests' && <CountRequestBadge />}
      {item.label === 'Pending' && <CountPendingBadge />}
      {item.label === 'Mentions' && <CountUnreadInboxBadge />}
    </>
  );

  return (
    <Menu.Item
      key={item.path}
      icon={iconWithBadge}
      title={item.label}
      className={selectedItem?.path === item.path ? 'app-menu-item-selected' : undefined}
      onClick={onClick}
      style={{ width: '100%', overflow: 'hidden' }}
    >
      {'externalUrl' in item && item.externalUrl ? (
        <a
          href={item.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ width: '100%', display: 'inline-flex', alignItems: 'center' }}
        >
          {linkContent}
        </a>
      ) : (
        <Link to={item.path} style={{ width: '100%', display: 'inline-flex', alignItems: 'center' }}>
          {linkContent}
        </Link>
      )}
    </Menu.Item>
  );
});

MenuItem.displayName = 'MenuItem';

export default MenuItem;
